/*
 * Related-colour swatches (collection thumbnails + PDP swatch selector).
 *
 * Previously this logic — including the full paginated catalog fetch and a
 * MutationObserver on document.body — was duplicated inline in every render
 * of snippets/product-thumbnail__swatch.liquid (i.e. once per product on a
 * collection grid) and again in snippets/product__swatch.liquid. That meant
 * a 24-product collection page shipped the same ~380 lines of script 24
 * times and could spin up 24 redundant MutationObservers. This file runs
 * once per page load instead, and is browser-cached across page views like
 * any other asset.
 */
(function ($) {
  if (!$) return;

  $(document).ready(function () {
    var THUMBNAIL_SELECTOR = '.thumbnail-related-products-container';
    var PDP_SELECTOR = '.related-products-container';

    // Nothing on this page needs the catalog at all — skip the fetch entirely.
    if ($(THUMBNAIL_SELECTOR).length === 0 && $(PDP_SELECTOR).length === 0) {
      return;
    }

    if (!window.globalProductsData) {
      window.globalProductsData = {
        products: [],
        isLoaded: false,
        isLoading: false,
        callbacks: [],
        processedContainers: new Set()
      };
    }

    // localStorage (not sessionStorage) so the cache survives across tabs and
    // browser restarts, not just the current tab's session. CACHE_VERSION lets
    // us invalidate old entries instantly if the cached shape ever changes,
    // without waiting on the TTL.
    var CACHE_VERSION = 2;
    var CACHE_KEY = 'kayserGlobalProductsCache_v' + CACHE_VERSION;
    var CACHE_TTL = 4 * 60 * 60 * 1000; // 4 hours

    function getStorage() {
      try {
        var testKey = '__kayser_storage_test__';
        window.localStorage.setItem(testKey, '1');
        window.localStorage.removeItem(testKey);
        return window.localStorage;
      } catch (e) {
        // Private browsing / storage disabled — fall back to sessionStorage,
        // which still helps repeat views within the same tab.
        return window.sessionStorage;
      }
    }

    function cacheGlobalProducts(products) {
      try {
        getStorage().setItem(CACHE_KEY, JSON.stringify({ products: products, timestamp: Date.now() }));
      } catch (e) {
        // Storage full/unavailable — safe to skip caching.
      }
    }

    // Fetch the full catalog once per page (or reuse a recent cached copy so
    // repeat page views — even in a new tab or after closing the browser —
    // skip the network fetch entirely).
    function fetchAllProductsGlobal() {
      if (window.globalProductsData.isLoaded) {
        return Promise.resolve(window.globalProductsData.products);
      }

      if (window.globalProductsData.isLoading) {
        return new Promise(function (resolve) {
          window.globalProductsData.callbacks.push(resolve);
        });
      }

      try {
        var cachedRaw = getStorage().getItem(CACHE_KEY);
        if (cachedRaw) {
          var cached = JSON.parse(cachedRaw);
          if (cached && cached.products && cached.products.length && (Date.now() - cached.timestamp) < CACHE_TTL) {
            window.globalProductsData.products = cached.products;
            window.globalProductsData.isLoaded = true;
            return Promise.resolve(cached.products);
          }
        }
      } catch (e) {
        // Corrupt/unavailable cache — fall through to a fresh fetch.
      }

      window.globalProductsData.isLoading = true;
      var allProducts = [];

      function fetchProductsPage(page) {
        return $.ajax({
          url: '/collections/all/products.json',
          method: 'GET',
          dataType: 'json',
          data: { limit: 250, page: page }
        }).then(function (data) {
          var pageProducts = (data && data.products) || [];
          allProducts = allProducts.concat(pageProducts);

          if (pageProducts.length === 250) {
            return fetchProductsPage(page + 1);
          }

          window.globalProductsData.products = allProducts;
          window.globalProductsData.isLoaded = true;
          window.globalProductsData.isLoading = false;
          cacheGlobalProducts(allProducts);

          window.globalProductsData.callbacks.forEach(function (callback) {
            callback(allProducts);
          });
          window.globalProductsData.callbacks = [];

          return allProducts;
        });
      }

      return fetchProductsPage(1).catch(function (error) {
        window.globalProductsData.isLoading = false;
        console.error('Failed to load related-colour catalog:', error);
        throw error;
      });
    }

    function getColorOptionIndex(product) {
      var colorOptionIndex = -1;
      (product.options || []).forEach(function (option, index) {
        var optionName = typeof option === 'object' ? option.name : option;
        if (
          optionName &&
          (optionName.toLowerCase().indexOf('color') !== -1 || optionName.toLowerCase().indexOf('colour') !== -1)
        ) {
          colorOptionIndex = index;
        }
      });
      return colorOptionIndex;
    }

    function getVariantColor(variant, colorOptionIndex) {
      if (colorOptionIndex === 0) return variant.option1;
      if (colorOptionIndex === 1) return variant.option2;
      if (colorOptionIndex === 2) return variant.option3;
      return null;
    }

    // Current-viewed colour first, then everything else, with "2 pack" bundle
    // colours pushed to the end (handle is the reliable signal — the colour
    // text itself is inconsistently formatted, e.g. "2pack-White" vs "2 pack-Sweet Pea").
    function sortRelatedProducts(relatedProducts, currentProduct) {
      relatedProducts.sort(function (a, b) {
        var aIsCurrent = a.handle === currentProduct;
        var bIsCurrent = b.handle === currentProduct;
        if (aIsCurrent !== bIsCurrent) return aIsCurrent ? -1 : 1;

        var aIsTwoPack = /2[-\s]?pack/i.test(a.handle);
        var bIsTwoPack = /2[-\s]?pack/i.test(b.handle);
        if (aIsTwoPack !== bIsTwoPack) return aIsTwoPack ? 1 : -1;

        return 0;
      });
    }

    function buildSwatchImageUrl(colorValue, fileExtension) {
      var imageName = colorValue.replace(/\//g, '-').replace(/---/g, '-').replace(/\s+/g, '-').toLowerCase() + '.' + fileExtension;
      imageName = imageName.replace(/---/g, '-');
      return 'https://www.kayserlingerie.com.au/cdn/shop/files/' + imageName;
    }

    // --- Collection grid thumbnail swatches -------------------------------

    function processThumbnailContainer(container) {
      var colorGroup = container.data('color-group');
      var currentProduct = container.data('current-product');
      var fileExtension = container.data('file-extension');
      var settingsShape = container.data('settings-shape');

      var relatedProducts = window.globalProductsData.products.filter(function (product) {
        return product.tags && product.tags.indexOf(colorGroup) !== -1;
      });

      if (relatedProducts.length === 0) {
        container.hide();
        return;
      }

      sortRelatedProducts(relatedProducts, currentProduct);

      var maxSwatches = 5;
      var totalUniqueColors = 0;
      var countedColors = new Set();

      relatedProducts.forEach(function (product2) {
        if (!product2.variants || !product2.options) return;
        var colorOptionIndex = getColorOptionIndex(product2);
        if (colorOptionIndex === -1) return;

        product2.variants.forEach(function (variant) {
          var colorValue = getVariantColor(variant, colorOptionIndex);
          if (colorValue && !countedColors.has(colorValue)) {
            countedColors.add(colorValue);
            totalUniqueColors++;
          }
        });
      });

      var swatchHTML = '';
      var seenColors = new Set();
      var swatchCount = 0;

      relatedProducts.forEach(function (product2) {
        if (swatchCount >= maxSwatches || !product2.variants || !product2.options) return;
        var colorOptionIndex = getColorOptionIndex(product2);
        if (colorOptionIndex === -1) return;

        product2.variants.forEach(function (variant) {
          if (swatchCount >= maxSwatches) return;
          var colorValue = getVariantColor(variant, colorOptionIndex);
          if (!colorValue || seenColors.has(colorValue)) return;
          seenColors.add(colorValue);

          swatchHTML += '<a href="/products/' + product2.handle + '" ';
          swatchHTML += 'class="swatch swatch__style--' + settingsShape + '" ';
          swatchHTML += 'data-swatch-name="meta-colour_' + colorValue.replace(/\s+/g, '_').toLowerCase() + '">';
          swatchHTML += '<span data-image="' + (product2.featured_image ? product2.featured_image.src : '') + '" ';
          swatchHTML +=
            'style="background-color: ' + colorValue.split(' ').pop().toLowerCase().replace(/\s+/g, '-') + ';">';
          swatchHTML +=
            '<img class="swatch__image " src="' +
            buildSwatchImageUrl(colorValue, fileExtension) +
            '" alt="' +
            colorValue +
            '" loading="lazy">';
          swatchHTML += '</span>';
          swatchHTML += '</a>';

          swatchCount++;
        });
      });

      if (totalUniqueColors > maxSwatches) {
        var additionalColors = totalUniqueColors - maxSwatches;
        swatchHTML += '<a class="swatch swatch__style--' + settingsShape + '">';
        swatchHTML += '<span style="background-color: transparent;">';
        swatchHTML += '<div class="swatch__image color__count">+' + additionalColors + '</div>';
        swatchHTML += '</span>';
        swatchHTML += '</a>';
      }

      if (swatchHTML.length > 0) {
        container.html(swatchHTML);
        container.show();
      } else {
        container.hide();
      }
    }

    function processThumbnailSwatches() {
      var containers = $(THUMBNAIL_SELECTOR);
      if (containers.length === 0) return;

      containers.each(function () {
        var container = $(this);
        var containerId = 'thumb_' + container.attr('data-color-group') + '_' + container.attr('data-current-product');
        if (window.globalProductsData.processedContainers.has(containerId)) return;

        processThumbnailContainer(container);
        window.globalProductsData.processedContainers.add(containerId);
      });
    }

    // --- PDP related-product colour swatches -------------------------------

    function processPdpContainer(container) {
      var colorGroup = container.data('color-group');
      var currentProduct = container.data('current-product');
      var optionIndex = container.data('option-index');
      var isColor = container.data('is-color') === 'true' || container.data('is-color') === true;
      var fileExtension = container.data('file-extension');
      var swatchName = container.data('swatch-name');

      if (!isColor && swatchName) {
        var downcasedSwatch = String(swatchName).toLowerCase();
        isColor = downcasedSwatch.indexOf('color') !== -1 || downcasedSwatch.indexOf('colour') !== -1;
      }

      if (!isColor) return;

      container.show();

      var relatedProducts = window.globalProductsData.products.filter(function (product) {
        return product.tags && product.tags.indexOf(colorGroup) !== -1;
      });

      sortRelatedProducts(relatedProducts, currentProduct);

      var swatchHTML = '';
      var uniqueColors = [];

      relatedProducts.forEach(function (product2) {
        if (!product2.variants || !product2.options) return;
        var colorOptionIndex = getColorOptionIndex(product2);
        if (colorOptionIndex === -1) return;

        product2.variants.forEach(function (variant) {
          var colorValue = getVariantColor(variant, colorOptionIndex);
          if (!colorValue || uniqueColors.indexOf(colorValue) !== -1) return;
          uniqueColors.push(colorValue);

          var isCurrentProduct = product2.handle === currentProduct;
          var swatchClass = isCurrentProduct ? 'checked' : '';
          var idSafeColor = colorValue.toLowerCase().replace(/\s+/g, '-');

          if (isCurrentProduct) {
            swatchHTML += '<input class="swatch--' + idSafeColor + ' product2" ';
            swatchHTML += 'id="swatch-' + optionIndex + '-' + idSafeColor + '-' + product2.id + '" ';
            swatchHTML += 'type="radio" aria-label="' + colorValue + '" tabindex="0" ';
            swatchHTML += 'name="option-' + optionIndex + '" value="' + colorValue + '" checked>';
          }

          var swatchId = 'swatch-' + optionIndex + '-' + idSafeColor + '-' + product2.id;
          var imageHTML =
            '<img class="swatch__image" src="' + buildSwatchImageUrl(colorValue, fileExtension) + '" alt="" width="50" height="50">';

          swatchHTML +=
            '<div class="swatch-element product2 relate-product ' + swatchClass + ' color ' + idSafeColor + '-swatch">';
          swatchHTML += '<div class="tooltip">' + colorValue + '</div>';

          // The current product's colour is the one already selected, so its
          // swatch stays a plain label bound to the radio above. Every other
          // colour lives on a different product, so its swatch links to that
          // product's page. The label wrapper is kept in both cases because the
          // swatch sizing/shape CSS targets `.swatch-element.color label`.
          if (isCurrentProduct) {
            swatchHTML += '<label for="' + swatchId + '">' + imageHTML + '</label>';
          } else {
            swatchHTML += '<label>';
            swatchHTML +=
              '<a class="swatch-element__link" href="/products/' +
              product2.handle +
              '" aria-label="' +
              colorValue +
              '">';
            swatchHTML += imageHTML;
            swatchHTML += '</a>';
            swatchHTML += '</label>';
          }

          swatchHTML += '</div>';
        });
      });

      if (swatchHTML.length > 0) {
        container.html(swatchHTML);
      } else {
        container.hide();
      }

      if (typeof window.initSwatches === 'function') {
        window.initSwatches();
      }
    }

    function processPdpSwatches() {
      var containers = $(PDP_SELECTOR);
      if (containers.length === 0) return;

      containers.each(function () {
        var container = $(this);
        var containerId = 'pdp_' + container.attr('data-color-group') + '_' + container.attr('data-current-product');
        if (window.globalProductsData.processedContainers.has(containerId)) return;

        processPdpContainer(container);
        window.globalProductsData.processedContainers.add(containerId);
      });
    }

    function processAllSwatches() {
      processThumbnailSwatches();
      processPdpSwatches();
    }

    // --- Dynamically-loaded content (infinite scroll, ajax filters, etc.) --

    var contentWatcherStarted = false;

    function handleDynamicContent() {
      if (!window.globalProductsData.isLoaded) return;
      processAllSwatches();
    }

    function setupContentWatcher() {
      if (contentWatcherStarted) return;
      contentWatcherStarted = true;

      var observer = new MutationObserver(function (mutations) {
        var hasNewContent = mutations.some(function (mutation) {
          if (mutation.type !== 'childList') return false;
          return Array.prototype.some.call(mutation.addedNodes, function (node) {
            if (node.nodeType !== 1) return false;
            var $node = $(node);
            return (
              $node.find(THUMBNAIL_SELECTOR + ', ' + PDP_SELECTOR).length > 0 ||
              $node.is(THUMBNAIL_SELECTOR) ||
              $node.is(PDP_SELECTOR)
            );
          });
        });

        if (hasNewContent) {
          setTimeout(handleDynamicContent, 100);
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });
    }

    fetchAllProductsGlobal()
      .then(function () {
        processAllSwatches();
        setupContentWatcher();
      })
      .catch(function (error) {
        console.error('Failed to load related-colour swatches:', error);
      });

    // Set up the watcher immediately too, in case content is added before
    // the initial fetch resolves.
    setupContentWatcher();
  });
})(window.jQuery);
